import type { LocaleCode } from './locales.js'
import { DEFAULT_LOCALE, LOCALE_CODES } from './locales.js'

/**
 * Picking a language from what the browser reports.
 *
 * Kept pure and separate from the i18n instance so the matching rules — which
 * are fiddlier than they look — can be tested directly.
 */

/**
 * BCP 47 tags that should resolve to a catalog whose code does not simply
 * match their primary subtag. Chinese is the awkward one: `zh-CN`, `zh-SG` and
 * bare `zh` are Simplified in practice, while `zh-TW`/`zh-HK`/`zh-Hant` are
 * Traditional — a script we do not ship, and showing Simplified to a
 * Traditional reader is a worse answer than English.
 */
const EXPLICIT_MATCHES: Record<string, LocaleCode> = {
  zh: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-hans': 'zh-Hans',
}

const UNSUPPORTED_SCRIPTS = ['zh-tw', 'zh-hk', 'zh-mo', 'zh-hant']

function matchOne(tag: string, supported: readonly LocaleCode[]): LocaleCode | null {
  const lower = tag.toLowerCase().trim()
  if (lower.length === 0) return null

  // A script we deliberately do not serve must not match its primary subtag.
  if (UNSUPPORTED_SCRIPTS.some((prefix) => lower === prefix || lower.startsWith(`${prefix}-`))) {
    return null
  }

  const exact = supported.find((code) => code.toLowerCase() === lower)
  if (exact) return exact

  for (const [pattern, code] of Object.entries(EXPLICIT_MATCHES)) {
    if ((lower === pattern || lower.startsWith(`${pattern}-`)) && supported.includes(code)) {
      return code
    }
  }

  // `de-AT` and `fr-CA` are served by `de` and `fr`.
  const primary = lower.split('-')[0]
  const byPrimary = supported.find((code) => code.toLowerCase() === primary)
  return byPrimary ?? null
}

export interface ResolveLocaleOptions {
  /** An explicit choice, which always wins. */
  stored?: string | null | undefined
  /** `navigator.languages`, most preferred first. */
  systemLanguages?: readonly string[]
  supported?: readonly LocaleCode[]
}

/**
 * Explicit choice first, then the system languages in order of preference,
 * then English.
 */
export function resolveLocale(options: ResolveLocaleOptions = {}): LocaleCode {
  const supported = options.supported ?? LOCALE_CODES

  if (options.stored) {
    const chosen = matchOne(options.stored, supported)
    if (chosen) return chosen
  }

  for (const tag of options.systemLanguages ?? []) {
    const chosen = matchOne(tag, supported)
    if (chosen) return chosen
  }

  return DEFAULT_LOCALE
}

/** `navigator.languages` with a defensive fallback for odd environments. */
export function systemLanguages(): string[] {
  if (typeof navigator === 'undefined') return []
  const list = navigator.languages
  if (Array.isArray(list) && list.length > 0) return [...list]
  return navigator.language ? [navigator.language] : []
}
